import React, { forwardRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import Input from 'src/components/ui/input';
import Select from 'src/components/ui/select';
import TextError from 'src/components/ui/text-error';

import globalStore from 'src/stores/global-store';

import { InputMask, InputMaxLength, LOCALES } from 'src/constants';
import { getDateMask } from 'src/utils';
import type { BurialPlaceFormProps } from './burial-place-form.props';

import './burial-place-form.scss';

const BurialPlaceForm = observer(
  forwardRef<HTMLDivElement, BurialPlaceFormProps>(({ formFields, formError, onChange }, ref) => {
    const { cemeteriesOptions, isLoading } = globalStore;
    const { cemetery, decedent, yearOfBirth, yearOfDeath } = formFields;
    const isSingleCemetery = cemeteriesOptions.length === 1;

    useEffect(() => {
      if (isSingleCemetery && !cemetery.value) {
        onChange('cemetery')(cemeteriesOptions[0].id);
      }
    }, [cemeteriesOptions, cemetery.value, isSingleCemetery, onChange]);

    return (
      <div className="burial-place-form" ref={ref}>
        <h2 className="burial-place-form__title">{LOCALES.FIND_LOCATION}</h2>
        <form className="burial-place-form__form">
          <Select
            className="burial-place-form__input burial-place-form__input_wide"
            isDisabled={isSingleCemetery}
            isLoading={isLoading}
            isValid={cemetery.isValid}
            items={cemeteriesOptions}
            label={LOCALES.CEMETERY}
            value={cemetery.value}
            onChange={onChange('cemetery')}
            textError={cemetery.textError}
          />
          <Input
            className="burial-place-form__input burial-place-form__input_wide"
            id="decedent"
            isLabelWithAsterisk
            isValid={decedent.isValid}
            label={LOCALES.DECEDENT}
            maxLength={InputMaxLength.TEXT}
            textError={decedent.textError}
            value={decedent.value}
            onChange={onChange('decedent')}
          />
          <Input
            className="burial-place-form__input"
            id="year-of-birth"
            inputMode="numeric"
            isValid={yearOfBirth.isValid}
            label={LOCALES.YEAR_OF_BIRTH}
            mask={InputMask.DATE}
            maskPlaceholder="Y"
            textError={yearOfBirth.textError}
            value={yearOfBirth.value}
            onChange={onChange('yearOfBirth')}
            renderCustomMaskText={getDateMask}
          />
          <Input
            className="burial-place-form__input"
            id="year-of-death"
            inputMode="numeric"
            isValid={yearOfDeath.isValid}
            label={LOCALES.YEAR_OF_DEATH}
            mask={InputMask.DATE}
            maskPlaceholder="Y"
            textError={yearOfDeath.textError}
            value={yearOfDeath.value}
            onChange={onChange('yearOfDeath')}
            renderCustomMaskText={getDateMask}
          />
        </form>

        {formError && !yearOfBirth.textError && !yearOfDeath.textError && (
          <TextError className="burial-place-form__error" textError={formError} />
        )}
      </div>
    );
  })
);

export default BurialPlaceForm;
